import React, { useMemo, useState } from "react";
import { PRIVACY_URL, TERMS_URL, TERMS_VERSION } from "../lib/legal";

const SUMMARY_POINTS = [
  {
    title: "Be respectful with data",
    detail:
      "Only upload lawful content, keep client information secure, and do not probe or misuse the service.",
  },
  {
    title: "Your data, your control",
    detail:
      "You own the data you store in InvoiceU. We process it to run the app and keep it safe, but you should keep your own backups.",
  },
  {
    title: "Billing basics",
    detail:
      "Paid plans renew automatically until you cancel. Fees are generally non-refundable unless required by law.",
  },
  {
    title: "Account changes",
    detail:
      "Deleting an account stops access immediately. We may retain limited records as required for legal or billing purposes.",
  },
];

const FirstTimeTermsPage = ({
  user,
  onAccept,
  onRetry,
  onSignOut,
  loading = false,
  error = null,
}) => {
  const [agreed, setAgreed] = useState(false);

  const displayName = useMemo(() => {
    const meta = user?.user_metadata || {};
    return (
      meta.full_name ||
      meta.name ||
      meta.given_name ||
      meta.family_name ||
      user?.email ||
      "there"
    );
  }, [user]);

  const handleAccept = async () => {
    if (!agreed || typeof onAccept !== "function") return;
    try {
      await onAccept();
    } catch (e) {
      // App owns error display; swallow here to avoid unhandled rejections.
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-black text-sm font-semibold text-white">
            IU
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              First-time sign-in
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">
              Welcome, {displayName}!
            </h1>
            <p className="text-sm text-slate-600">
              Before you continue, please review and accept our Terms &
              Conditions and privacy commitments.
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Key points (summary)
            </p>
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-slate-900 underline"
            >
              Read full terms
            </a>
          </div>
          <div className="max-h-48 space-y-3 overflow-y-auto pr-1 text-sm text-slate-700">
            {SUMMARY_POINTS.map((item) => (
              <div
                key={item.title}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2"
              >
                <p className="text-sm font-semibold text-slate-900">
                  {item.title}
                </p>
                <p className="text-sm text-slate-600">{item.detail}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            This summary is not a substitute for the full Terms & Conditions.
            Please review the full policy for all details.
          </p>
        </div>

        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <input
            id="terms-agree"
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-black focus:ring-black"
            checked={agreed}
            disabled={loading}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <label htmlFor="terms-agree" className="leading-snug">
            I agree to the{" "}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-slate-900 underline"
            >
              Terms & Conditions
            </a>{" "}
            (version {TERMS_VERSION}) and the{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-slate-900 underline"
            >
              Privacy Practices
            </a>{" "}
            described there. I understand InvoiceU will use my information to
            provide the service.
          </label>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 flex-wrap gap-2">
            <button
              type="button"
              onClick={handleAccept}
              disabled={!agreed || loading}
              className={[
                "flex-1 rounded-full px-4 py-2 text-sm font-semibold sm:flex-none sm:min-w-[180px]",
                agreed && !loading
                  ? "bg-black text-white hover:bg-slate-900"
                  : "cursor-not-allowed bg-slate-200 text-slate-500",
              ].join(" ")}
            >
              {loading ? "Saving your acceptance…" : "Accept & Continue"}
            </button>
            <button
              type="button"
              onClick={onRetry}
              disabled={loading}
              className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Reload check
            </button>
          </div>
          <button
            type="button"
            onClick={onSignOut}
            disabled={loading}
            className="text-xs font-semibold text-slate-500 underline hover:text-slate-700 disabled:cursor-not-allowed"
          >
            Sign out instead
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstTimeTermsPage;
